/**
 * AdminShell — the authenticated frame on ui-web's AppShell (M6-01, §14.4):
 * skip link first, SideNav at the inline start (mirrors in AR with zero app
 * code, §14.2), TopBar, routed content. Route content is lazy-loaded
 * (§11.2); Suspense shows skeletons, never a blank page or spinner (§14.3).
 */

import { Suspense, useCallback, useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import { AppShell, Skeleton } from "@platform/ui-web";
import { AdminSideNav } from "./Sidebar";
import { AdminTopbar } from "./Topbar";
import { useI18n } from "../i18n/I18nProvider";

const COLLAPSED_KEY = "admin.nav.collapsed";

/*
 * Below this width the fixed-width ui-web SideNav (16.5rem) leaves too little
 * room for content and pushes the page into horizontal overflow (M7-30 — the
 * shell was only ever validated at the login page, which has no nav). The panel
 * is desktop-first (§14.3), so rather than a mobile drawer we auto-collapse the
 * existing nav to its icon rail (4.5rem) at narrow widths using the shell's own
 * `navCollapsed` prop — no `packages/` change, and the user's manual preference
 * is still honoured once the viewport is wide enough to hold the full nav.
 */
const NARROW_QUERY = "(max-width: 48rem)";

function readCollapsed(): boolean {
  try {
    return window.localStorage.getItem(COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
}

/** True when the viewport is too narrow to hold the expanded nav without overflow. */
function useNarrowViewport(): boolean {
  const supported = typeof window !== "undefined" && typeof window.matchMedia === "function";
  const [narrow, setNarrow] = useState(() => (supported ? window.matchMedia(NARROW_QUERY).matches : false));
  useEffect(() => {
    if (!supported) return;
    const mq = window.matchMedia(NARROW_QUERY);
    const onChange = () => {
      setNarrow(mq.matches);
    };
    onChange();
    mq.addEventListener("change", onChange);
    return () => {
      mq.removeEventListener("change", onChange);
    };
  }, [supported]);
  return narrow;
}

function RouteSkeleton() {
  return (
    <div className="shell-route-skeleton" aria-hidden="true">
      <Skeleton height="2rem" width="30%" />
      <Skeleton height="1rem" width="55%" />
      <Skeleton height="14rem" />
    </div>
  );
}

export function AdminShell() {
  const { t } = useI18n();
  const narrow = useNarrowViewport();
  const [userCollapsed, setUserCollapsed] = useState(readCollapsed);
  // Narrow viewports force the icon rail; wider ones follow the saved preference.
  const collapsed = narrow || userCollapsed;

  const toggleCollapsed = useCallback(() => {
    setUserCollapsed((value) => {
      const next = !value;
      try {
        window.localStorage.setItem(COLLAPSED_KEY, next ? "1" : "0");
      } catch {
        /* preference only */
      }
      return next;
    });
  }, []);

  return (
    <AppShell
      skipToContentLabel={t("app.skipToContent")}
      navCollapsed={collapsed}
      nav={<AdminSideNav />}
      topBar={<AdminTopbar navCollapsed={collapsed} onToggleNav={toggleCollapsed} />}
    >
      <Suspense fallback={<RouteSkeleton />}>
        <Outlet />
      </Suspense>
    </AppShell>
  );
}
